import React, { useState, useEffect } from 'react';
import Icon          from '../components/ui/Icon';
import Button        from '../components/ui/Button';
import Input         from '../components/ui/Input';
import PullToRefresh from '../components/ui/PullToRefresh';
import { useProperties, useCreateProperty } from '../hooks/useProperties';
import { useEnabledLocations } from '../hooks/useLocations';

/* ── Constants ──────────────────────────────────────────────────────────── */
const PROPERTY_TYPES = [
  { label: 'Airbnb',       value: 'airbnb'       },
  { label: 'Private Home', value: 'private_home' },
];

const EMPTY_FORM = {
  property_name:    '',
  property_type:    'airbnb',
  property_link:    '',
  property_address: '',
  address_second:   '',
  city:             '',
  state:            '',
  zip_code:         '',
  country:          '',
};

/* ── Helpers ────────────────────────────────────────────────────────────── */
const validate = (f) => {
  const e = {};
  if (!f.property_name.trim())    e.property_name    = 'Property name is required';
  if (f.property_link.trim() && !/^https?:\/\/.+/.test(f.property_link.trim()))
    e.property_link = 'Enter a valid URL (https://...)';
  if (!f.property_address.trim()) e.property_address = 'Address is required';
  if (!f.city.trim())             e.city             = 'City is required';
  if (!f.state.trim())            e.state            = 'State is required';
  if (!f.country.trim())          e.country          = 'Country is required';
  return e;
};

const typeLabel = (value) =>
  PROPERTY_TYPES.find((t) => t.value === value)?.label ?? value;

/* ── Property Card ──────────────────────────────────────────────────────── */
const PropertyCard = ({ property, onView }) => {
  const addressLine = [
    property.property_address,
    property.address_second,
    property.city,
    property.state,
    property.zip_code,
    property.country,
  ].filter(Boolean).join(', ');

  return (
    <div
      onClick={() => onView(property)}
      className="rounded-3xl bg-white border border-surface-500 card-shadow overflow-hidden animate-fadeUp cursor-pointer active:scale-[0.98] transition-transform"
      style={{ animationFillMode: 'forwards' }}
    >
      <div className="h-2" style={{ background: 'linear-gradient(90deg, #4338ca, #6366f1, #818cf8)' }} />

      <div className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1 min-w-0">
            <h3 className="font-syne font-bold text-slate-800 text-base leading-tight truncate">
              {property.property_name}
            </h3>
            <span
              className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full text-xs font-dm font-semibold text-brand-600"
              style={{ background: 'rgba(99,102,241,0.10)', border: '1px solid rgba(99,102,241,0.20)' }}
            >
              {typeLabel(property.property_type)}
            </span>
          </div>
        </div>

        <div className="flex items-start gap-1.5 mb-3">
          <Icon name="location" size={12} className="text-brand-500 flex-shrink-0 mt-0.5" />
          <p className="text-xs font-dm text-surface-100 leading-relaxed line-clamp-2">
            {addressLine}
          </p>
        </div>

        {property.property_link && (
          <div
            className="flex items-center gap-1.5 mb-3"
            onClick={(e) => { e.stopPropagation(); window.open(property.property_link, '_blank', 'noopener,noreferrer'); }}
          >
            <Icon name="link" size={12} className="text-brand-500 flex-shrink-0" />
            <p className="text-xs font-dm text-brand-600 truncate hover:underline">
              {property.property_link}
            </p>
          </div>
        )}

        <div className="flex items-end justify-between gap-3 pt-3 border-t border-surface-600">
          <div className="flex flex-col gap-0.5">
            <span className="text-xs font-dm font-semibold text-surface-200 uppercase tracking-wider">
              Property ID
            </span>
            <span className="text-xs font-dm font-medium text-slate-700 break-all">
              {property.property_id ?? property.id ?? '—'}
            </span>
          </div>

          {property.qr_code && (
            <div className="flex flex-col items-center gap-0.5 flex-shrink-0">
              <span className="text-xs font-dm font-semibold text-surface-200 uppercase tracking-wider">
                QR Code
              </span>
              <img
                src={property.qr_code}
                alt="QR Code"
                className="w-14 h-14 rounded-xl border border-surface-400 object-contain bg-white p-0.5"
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/* ── Property Detail Modal ──────────────────────────────────────────────── */
const ANIM_DURATION = 280;

const PropertyDetailModal = ({ property, onClose }) => {
  const [closing, setCl] = useState(false);
  const [copied,  setCopied] = useState(false);

  const propertyId = property.property_id ?? property.id ?? '';

  const handleClose = () => { setCl(true); setTimeout(onClose, ANIM_DURATION); };

  const handleCopy = () => {
    navigator.clipboard.writeText(String(propertyId)).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleDownloadQr = async () => {
    if (!property.qr_code) return;
    try {
      const res  = await fetch(property.qr_code);
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `${property.property_name ?? 'qr-code'}.png`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      window.open(property.qr_code, '_blank');
    }
  };

  return (
    <>
      <div
        className={`fixed inset-0 bg-black/50 z-40 ${closing ? 'animate-backdropOut' : 'animate-backdropIn'}`}
        onClick={handleClose}
      />
      <div
        className={`fixed bottom-0 -translate-x-1/2 w-full max-w-[430px] z-50 bg-surface-900 rounded-t-3xl ${closing ? 'animate-sheetDown' : 'animate-sheetUp'}`}
        style={{ boxShadow: '0 -8px 40px rgba(0,0,0,0.25)' }}
      >
        <div className="flex justify-center pt-3 pb-1">
          <div className="w-10 h-1 rounded-full bg-surface-500" />
        </div>

        <div className="px-5 pb-8 pt-2">
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-syne font-extrabold text-slate-800 text-lg leading-tight truncate pr-3">
              {property.property_name}
            </h2>
            <button
              onClick={handleClose}
              className="w-8 h-8 rounded-xl bg-surface-700 border border-surface-400 flex items-center justify-center text-surface-200 press-scale flex-shrink-0"
            >
              <Icon name="x" size={16} />
            </button>
          </div>

          {property.qr_code ? (
            <div className="flex flex-col items-center mb-5">
              <div className="p-3 bg-white rounded-2xl border border-surface-400 shadow-sm">
                <img src={property.qr_code} alt="QR Code" className="w-48 h-48 object-contain" />
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-32 mb-5 rounded-2xl border border-dashed border-surface-400">
              <p className="text-xs font-dm text-surface-200">No QR code available</p>
            </div>
          )}

          <div className="flex items-center justify-between gap-3 bg-surface-800 border border-surface-500 rounded-2xl px-4 py-3 mb-4">
            <div className="flex flex-col gap-0.5 min-w-0">
              <span className="text-xs font-dm font-semibold text-surface-200 uppercase tracking-wider">
                Property ID
              </span>
              <span className="text-sm font-dm font-medium text-slate-700 truncate">
                {propertyId || '—'}
              </span>
            </div>
            <button
              onClick={handleCopy}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-dm font-semibold border press-scale transition-all flex-shrink-0 ${
                copied
                  ? 'bg-emerald-50 border-emerald-300 text-emerald-600'
                  : 'bg-surface-700 border-surface-400 text-surface-100 hover:border-brand-500'
              }`}
            >
              <Icon name={copied ? 'check' : 'copy'} size={12} />
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>

          {property.property_link && (
            <a
              href={property.property_link}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 bg-surface-800 border border-surface-500 rounded-2xl px-4 py-3 mb-4 group"
            >
              <div className="flex flex-col gap-0.5 min-w-0 flex-1">
                <span className="text-xs font-dm font-semibold text-surface-200 uppercase tracking-wider">
                  Property Link
                </span>
                <span className="text-sm font-dm font-medium text-brand-600 truncate group-hover:underline">
                  {property.property_link}
                </span>
              </div>
              <Icon name="link" size={14} className="text-brand-500 flex-shrink-0" />
            </a>
          )}

          <button
            onClick={handleDownloadQr}
            disabled={!property.qr_code}
            className="w-full h-[52px] rounded-2xl font-syne font-semibold text-sm text-white flex items-center justify-center gap-2 press-scale disabled:opacity-40 transition-all"
            style={{ background: 'linear-gradient(135deg, #4338ca 0%, #6366f1 60%, #818cf8 100%)' }}
          >
            <Icon name="download" size={16} />
            Download QR Code
          </button>
        </div>
      </div>
    </>
  );
};

/* ── Add Property Sheet ─────────────────────────────────────────────────── */
const AddPropertySheet = ({ onClose, onCreate }) => {
  const [closing, setCl]     = useState(false);
  const [form,    setForm]   = useState(EMPTY_FORM);
  const [errors,  setErrors] = useState({});
  const { data: countries = [], isLoading: locationsLoading } = useEnabledLocations();

  useEffect(() => {
    if (countries.length === 1)
      setForm((f) => f.country ? f : { ...f, country: countries[0].country_name });
  }, [countries]);

  const handleClose = () => { setCl(true); setTimeout(onClose, ANIM_DURATION); };

  const set = (field) => (e) => {
    setForm((f) => ({ ...f, [field]: e.target.value }));
    if (errors[field]) setErrors((prev) => { const n = { ...prev }; delete n[field]; return n; });
  };

  const handleSubmit = () => {
    const e = validate(form);
    if (Object.keys(e).length) { setErrors(e); return; }
    onCreate(form);
  };

  return (
    <>
      <div
        className={`fixed inset-0 bg-black/40 z-40 ${closing ? 'animate-backdropOut' : 'animate-backdropIn'}`}
        onClick={handleClose}
      />
      <div
        className={`fixed bottom-0 -translate-x-1/2 w-full max-w-[430px] z-50 bg-surface-900 rounded-t-3xl ${closing ? 'animate-sheetDown' : 'animate-sheetUp'}`}
        style={{ maxHeight: '92vh', overflowY: 'auto', boxShadow: '0 -8px 40px rgba(0,0,0,0.25)' }}
      >
        <div className="flex justify-center pt-3 pb-1">
          <div className="w-10 h-1 rounded-full bg-surface-500" />
        </div>

        <div className="px-5 pb-10">
          <div className="flex items-center justify-between mb-5 mt-2">
            <h2 className="font-syne font-extrabold text-slate-800 text-lg">Add Property</h2>
            <button
              onClick={handleClose}
              className="w-8 h-8 rounded-xl bg-surface-700 border border-surface-400 flex items-center justify-center text-surface-200 press-scale"
            >
              <Icon name="x" size={16} />
            </button>
          </div>

          <div className="flex flex-col gap-3">
            <Input
              label="Property Name" name="property_name" placeholder="e.g. Beachside Villa"
              value={form.property_name} onChange={set('property_name')}
              error={errors.property_name} icon="building"
            />

            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-dm font-medium text-surface-100 uppercase tracking-wider">
                Property Type
              </label>
              <div className="flex flex-wrap gap-2">
                {PROPERTY_TYPES.map((t) => (
                  <button
                    key={t.value} type="button"
                    onClick={() => setForm((f) => ({ ...f, property_type: t.value }))}
                    className={`px-3 py-1.5 rounded-xl text-xs font-dm font-semibold border transition-all press-scale ${
                      form.property_type === t.value
                        ? 'bg-brand-600 border-brand-600 text-white'
                        : 'bg-surface-700 border-surface-400 text-surface-100 hover:border-brand-500'
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            <Input
              label="Property Link (optional)" name="property_link"
              placeholder="https://www.airbnb.com/rooms/12345678"
              value={form.property_link} onChange={set('property_link')}
              error={errors.property_link} icon="link"
            />

            <Input
              label="Property Address" name="property_address" placeholder="123 Palm Street"
              value={form.property_address} onChange={set('property_address')}
              error={errors.property_address} icon="location"
            />

            <Input
              label="Address Line 2 (optional)" name="address_second"
              placeholder="Floor 2, Unit 4B, Near metro station…"
              value={form.address_second} onChange={set('address_second')}
            />

            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-dm font-medium text-surface-100 uppercase tracking-wider">
                Country
              </label>
              <select
                value={form.country}
                onChange={set('country')}
                disabled={locationsLoading}
                className={`h-[52px] w-full rounded-2xl bg-surface-600 border text-sm font-dm text-slate-800 px-4 transition-all duration-200 focus:outline-none disabled:opacity-50 ${
                  errors.country
                    ? 'border-rose-400 focus:border-rose-500 focus:ring-2 focus:ring-rose-500/15'
                    : 'border-surface-400 focus:border-brand-500 focus:ring-2 focus:ring-brand-500/15'
                }`}
              >
                <option value="">Select country</option>
                {countries.map((c) => (
                  <option key={c.country_code} value={c.country_name}>
                    {c.country_name}
                  </option>
                ))}
              </select>
              {errors.country && (
                <p className="text-xs text-rose-500 font-dm flex items-center gap-1">
                  <span>⚠</span> {errors.country}
                </p>
              )}
            </div>

            <div className="flex gap-3">
              <Input label="State" name="state" placeholder="e.g. California"
                value={form.state} onChange={set('state')} error={errors.state} className="flex-1" />
              <Input label="City" name="city" placeholder="e.g. Dubai"
                value={form.city} onChange={set('city')} error={errors.city} className="flex-1" />
            </div>

            <Input label="ZIP Code" name="zip_code" placeholder="e.g. 90210"
              value={form.zip_code} onChange={set('zip_code')} />

            <div className="flex gap-3 mt-1">
              <Button variant="ghost" onClick={handleClose} className="flex-1">Cancel</Button>
              <Button onClick={handleSubmit} className="flex-1">
                <Icon name="plus" size={15} />
                Add Property
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

/* ── Main Page ──────────────────────────────────────────────────────────── */
const PropertiesPage = () => {
  const { data, isLoading, isError, refetch } = useProperties();
  const createMutation = useCreateProperty();

  const [showAdd,    setShowAdd]    = useState(false);
  const [viewTarget, setViewTarget] = useState(null);

  const properties = data?.properties ?? data ?? [];

  const handleCreate = (payload) =>
    createMutation.mutate(payload, { onSuccess: () => setShowAdd(false) });

  return (
    <PullToRefresh onRefresh={refetch}>
      <div className="flex flex-col min-h-full bg-surface-900 relative">
        <div className="noise-overlay" />
        <div
          className="absolute top-0 right-0 w-56 h-56 rounded-full pointer-events-none opacity-[0.04]"
          style={{ background: 'radial-gradient(circle, #6366f1 0%, transparent 70%)', transform: 'translate(30%, -30%)' }}
        />

        <div className="flex-1 px-4 pt-4 pb-6">
          <button
            onClick={() => setShowAdd(true)}
            className="w-full flex items-center justify-center gap-2 h-[52px] rounded-2xl mb-5 font-syne font-semibold text-sm text-white press-scale brand-glow"
            style={{ background: 'linear-gradient(135deg, #4338ca 0%, #6366f1 60%, #818cf8 100%)' }}
          >
            <Icon name="plus" size={18} />
            Add New Property
          </button>

          {isLoading && (
            <div className="flex flex-col gap-3">
              {[1, 2, 3].map((n) => (
                <div key={n} className="rounded-3xl bg-white border border-surface-500 card-shadow overflow-hidden">
                  <div className="h-2 shimmer-bg" />
                  <div className="p-4">
                    <div className="h-4 w-36 rounded-md shimmer-bg mb-2" />
                    <div className="h-4 w-20 rounded-full shimmer-bg mb-3" />
                    <div className="h-3 w-48 rounded-md shimmer-bg mb-3" />
                    <div className="pt-3 border-t border-surface-600">
                      <div className="h-2.5 w-24 rounded-md shimmer-bg" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {isError && (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="w-14 h-14 rounded-2xl bg-rose-50 border border-rose-200 flex items-center justify-center mb-3">
                <Icon name="help" size={22} className="text-rose-400" />
              </div>
              <p className="font-dm text-sm text-surface-200">Failed to load properties.</p>
            </div>
          )}

          {!isLoading && !isError && properties.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16 text-center animate-fadeUp" style={{ animationFillMode: 'forwards' }}>
              <div
                className="w-20 h-20 rounded-3xl flex items-center justify-center mb-5"
                style={{ background: 'linear-gradient(135deg, #4338ca 0%, #6366f1 60%, #818cf8 100%)' }}
              >
                <Icon name="building" size={32} className="text-white" />
              </div>
              <h3 className="font-syne font-bold text-slate-800 text-base mb-2">No Properties Yet</h3>
              <p className="font-dm text-sm text-surface-200 max-w-[220px]">
                Add your first property to start managing your listings.
              </p>
            </div>
          )}

          {!isLoading && !isError && properties.length > 0 && (
            <div className="flex flex-col gap-3">
              {properties.map((p) => (
                <PropertyCard
                  key={p.property_id ?? p.id}
                  property={p}
                  onView={(prop) => setViewTarget(prop)}
                />
              ))}
            </div>
          )}
        </div>

        {viewTarget && (
          <PropertyDetailModal
            property={viewTarget}
            onClose={() => setViewTarget(null)}
          />
        )}

        {showAdd && (
          <AddPropertySheet
            onClose={() => setShowAdd(false)}
            onCreate={handleCreate}
          />
        )}
      </div>
    </PullToRefresh>
  );
};

export default PropertiesPage;
